/**
 * @module
 * Contains the ReplaceableStore interface and a default localStorage-based implementation.
 */ const STORAGE_KEY = '@nostr/gadgets/replaceables';
function isAddressable(kind) {
  return kind >= 30000 && kind < 40000;
}
/**
 * A ReplaceableStore implementation using window.localStorage.
 * All data stored in a single key with structure: {pubkey: {kind: [lastAttempt, event(s)]}}
 * For regular replaceables: [lastAttempt, event | undefined]
 * For 3xxxx kinds: [lastAttempt, events[]] (bundle)
 */ export class LocalStorageReplaceableStore {
  loadStorage() {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch  {
      return {};
    }
  }
  saveStorage(data) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
  async loadReplaceables(specs) {
    const storage = this.loadStorage();
    return specs.map(([kind, pubkey, dtag])=>{
      const pubkeyData = storage[pubkey];
      if (!pubkeyData) {
        return isAddressable(kind) && dtag === undefined ? [
          undefined,
          []
        ] : [
          undefined,
          undefined
        ];
      }
      const entry = pubkeyData[kind];
      if (!entry) {
        return isAddressable(kind) && dtag === undefined ? [
          undefined,
          []
        ] : [
          undefined,
          undefined
        ];
      }
      if (isAddressable(kind) && dtag === undefined) {
        // for 3xxxx kinds without dtag, return bundle (array of all events)
        const [lastAttempt, events] = entry;
        return [
          lastAttempt,
          events
        ];
      } else if (isAddressable(kind)) {
        // for 3xxxx kinds with dtag, return single event from bundle
        const [lastAttempt, events] = entry;
        const event = events.find((e)=>(e.tags.find((t)=>t[0] === 'd')?.[1] || '') === dtag);
        return [
          lastAttempt,
          event
        ];
      } else {
        // regular replaceable - single event storage
        const [lastAttempt, event] = entry;
        // skip placeholder events with all-zero IDs
        if (event && event.id === '0'.repeat(64)) {
          return [
            lastAttempt,
            undefined
          ];
        }
        return [
          lastAttempt,
          event
        ];
      }
    });
  }
  async saveEvent(event, options) {
    const storage = this.loadStorage();
    const now = options?.lastAttempt || Math.round(Date.now() / 1000);
    const kind = event.kind;
    const pubkey = event.pubkey;
    if (!storage[pubkey]) {
      storage[pubkey] = {};
    }
    if (isAddressable(kind)) {
      const dtag = event.tags.find((t)=>t[0] === 'd')?.[1] || '';
      // for 3xxxx kinds, store in bundle
      let bundle = storage[pubkey][kind];
      if (!bundle) {
        bundle = [
          now,
          []
        ];
      }
      // handle placeholder events with all-zero IDs - just update lastAttempt
      if (event.id === '0'.repeat(64) && event.created_at === 0) {
        bundle[0] = now;
        storage[pubkey][kind] = bundle;
        this.saveStorage(storage);
        return false;
      }
      // find existing event with same dtag
      const existingIndex = bundle[1].findIndex((e)=>(e.tags.find((t)=>t[0] === 'd')?.[1] || '') === dtag);
      if (existingIndex >= 0) {
        const existing = bundle[1][existingIndex];
        if (existing.created_at >= event.created_at) {
          // Existing is newer, just update lastAttempt
          bundle[0] = now;
          storage[pubkey][kind] = bundle;
          this.saveStorage(storage);
          return false;
        }
        // replace with newer event
        bundle[1][existingIndex] = event;
      } else {
        // add new event to bundle
        bundle[1].push(event);
      }
      bundle[0] = now;
      storage[pubkey][kind] = bundle;
      this.saveStorage(storage);
      return true;
    } else {
      // regular replaceable - single event storage
      const existing = storage[pubkey][kind];
      // handle placeholder events with all-zero IDs - just save lastAttempt marker
      if (event.id === '0'.repeat(64) && event.created_at === 0) {
        if (existing) {
          existing[0] = now;
          storage[pubkey][kind] = existing;
        } else {
          storage[pubkey][kind] = [
            now,
            event
          ];
        }
        this.saveStorage(storage);
        return false;
      }
      // check if we already have a newer event
      if (existing && existing[1] && existing[1].created_at >= event.created_at) {
        // update lastAttempt
        existing[0] = now;
        storage[pubkey][kind] = existing;
        this.saveStorage(storage);
        return false;
      }
      storage[pubkey][kind] = [
        now,
        event
      ];
      this.saveStorage(storage);
      return true;
    }
  }
  async deleteReplaceable(kind, pubkey) {
    const storage = this.loadStorage();
    if (storage[pubkey]) {
      delete storage[pubkey][kind];
      // clean up empty pubkey objects
      if (Object.keys(storage[pubkey]).length === 0) {
        delete storage[pubkey];
      }
      this.saveStorage(storage);
    }
  }
}
/**
 * Default replaceable store instance using localStorage.
 */ export const defaultReplaceableStore = new LocalStorageReplaceableStore();
//# sourceMappingURL=replaceable-store.js.map